
import { Coordinates } from '../types';

// ——— Types ———

export type FishSpecies = 'Ørret' | 'Røye' | 'Laks' | 'Abbor' | 'Gjedde';

export interface FishObservation {
  id: string;
  species: FishSpecies;
  location: Coordinates;
  observedAt: Date | null;
  locality: string | null;
  municipality: string | null;
  detailUrl: string | null;
}

export interface FishAbundanceCluster {
  center: Coordinates;
  totalObservations: number;
  speciesBreakdown: { species: string; count: number }[];
  scoreLabel: string;
  scoreLevel: 0 | 1 | 2 | 3;
  color: string;
}

// ——— Fish species matching ———

const FISH_PATTERNS: { species: FishSpecies; patterns: string[] }[] = [
  { species: 'Ørret',  patterns: ['orret', 'ørret', 'ouring', 'salmo trutta'] },
  { species: 'Røye',   patterns: ['roye', 'røye', 'roding', 'salvelinus alpinus'] },
  { species: 'Laks',   patterns: ['laks', 'salmo salar'] },
  { species: 'Abbor',  patterns: ['abbor', 'aborre', 'perca fluviatilis'] },
  { species: 'Gjedde', patterns: ['gjedde', 'gedde', 'esox lucius'] },
];

// Scientific names for GBIF queries
const FISH_SCIENTIFIC_NAMES = [
  'Salmo trutta', 'Salvelinus alpinus', 'Salmo salar',
  'Perca fluviatilis', 'Esox lucius',
];

const normalize = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

const getFishSpecies = (obs: any): FishSpecies | null => {
  const names = [
    obs?.Name, obs?.vernacularName, obs?.vernacularNameNb, obs?.vernacularNameBokmal,
    obs?.taxonVernacularName, obs?.taxonName, obs?.speciesName, obs?.name,
    obs?.ScientificName, obs?.scientificName,
    obs?.taxon?.vernacularName, obs?.taxon?.vernacularNameNb,
    obs?.taxon?.scientificName, obs?.taxon?.name,
  ].filter(Boolean).map((v: any) => normalize(String(v)));

  for (const { species, patterns } of FISH_PATTERNS) {
    if (patterns.some(p => names.some(n => n.includes(p)))) return species;
  }
  return null;
};

// ——— Coordinate & date parsing (mirrors useWildlifeLayer.ts) ———

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = Number(value.replace(',', '.').trim());
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
};

const parseLocation = (obs: any): Coordinates | null => {
  const lat = toNumber(
    obs?.decimalLatitude ?? obs?.lat ?? obs?.latitude ??
    obs?.location?.lat ?? obs?.location?.latitude ?? obs?.Latitude ??
    obs?.coordinates?.lat ?? obs?.coordinates?.latitude
  );
  const lon = toNumber(
    obs?.decimalLongitude ?? obs?.lon ?? obs?.long ?? obs?.lng ?? obs?.longitude ??
    obs?.location?.lon ?? obs?.location?.lng ?? obs?.location?.longitude ??
    obs?.Longitude ?? obs?.coordinates?.lon ?? obs?.coordinates?.lng ?? obs?.coordinates?.longitude
  );
  if (!isFinite(lat) || !isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { latitude: lat, longitude: lon };
};

const parseDate = (obs: any): Date | null => {
  const raw = obs?.observedAt || obs?.observationDate || obs?.eventDate ||
    obs?.dateObserved || obs?.createdAt || obs?.date || obs?.TrackDateTime;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

// ——— WKT polygon builder ———

const PROXY_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy';
const FISH_RADIUS_METERS = 1000;
const FISH_LOOKBACK_YEARS = 5;

const toWebMercator = (lat: number, lon: number): { x: number; y: number } => {
  const r = 6378137;
  return {
    x: (lon * Math.PI / 180) * r,
    y: Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 360))) * r,
  };
};

const buildGmWktPolygon = (center: Coordinates, radiusMeters: number): string => {
  const m = toWebMercator(center.latitude, center.longitude);
  const x1 = m.x - radiusMeters;
  const x2 = m.x + radiusMeters;
  const y1 = m.y - radiusMeters;
  const y2 = m.y + radiusMeters;
  return `POLYGON((${x1} ${y1},${x2} ${y1},${x2} ${y2},${x1} ${y2},${x1} ${y1}))`;
};

// ——— Scoring ———

export const computeFishScore = (totalObs: number): { label: string; level: 0 | 1 | 2 | 3; color: string } => {
  if (totalObs === 0) return { label: 'Ingen data', level: 0, color: '#9ca3af' };
  if (totalObs <= 10) return { label: 'Sjelden observasjon', level: 1, color: '#93c5fd' };
  if (totalObs <= 100) return { label: 'Registrert bestand', level: 2, color: '#3b82f6' };
  return { label: 'God bestand / Mye fisk', level: 3, color: '#1e3a5f' };
};

// ——— Fetch fish observations from Artsdatabanken ———

export const fetchFishObservations = async (
  center: Coordinates,
  signal?: AbortSignal
): Promise<FishObservation[]> => {
  const to = new Date();
  const from = new Date();
  from.setFullYear(from.getFullYear() - FISH_LOOKBACK_YEARS);

  const params = new URLSearchParams({
    pageIndex: '0',
    pageSize: '600',
    fromDate: from.toISOString().slice(0, 10),
    toDate: to.toISOString().slice(0, 10),
    gmWktPolygon: buildGmWktPolygon(center, FISH_RADIUS_METERS),
  });

  const sourceUrl = `https://artskart.artsdatabanken.no/publicapi/api/observations/list?${params.toString()}`;
  const proxyUrl = `${PROXY_URL}?url=${encodeURIComponent(sourceUrl)}`;

  const response = await fetch(proxyUrl, {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`fishing_${response.status}`);
  const json = await response.json();

  const rawList: any[] = Array.isArray(json) ? json
    : Array.isArray(json?.data) ? json.data
    : Array.isArray(json?.Observations) ? json.Observations
    : Array.isArray(json?.results) ? json.results
    : Array.isArray(json?.observations) ? json.observations
    : [];

  const results: FishObservation[] = [];
  const seen = new Set<string>();

  rawList.forEach((obs, idx) => {
    const species = getFishSpecies(obs);
    const loc = parseLocation(obs);
    if (!species || !loc) return;

    const key = `${species}-${loc.latitude.toFixed(3)}-${loc.longitude.toFixed(3)}`;
    if (seen.has(key)) return;
    seen.add(key);

    results.push({
      id: String(obs?.Id || obs?.id || obs?.observationId || `fish_${idx}`),
      species,
      location: loc,
      observedAt: parseDate(obs),
      locality: typeof obs?.Locality === 'string' ? obs.Locality : null,
      municipality: typeof obs?.Municipality === 'string' ? obs.Municipality : null,
      detailUrl: typeof obs?.DetailUrl === 'string' ? obs.DetailUrl
        : (typeof obs?.ObsUrl === 'string' ? obs.ObsUrl : null),
    });
  });

  return results;
};

// ——— Fetch fish observations from GBIF (Sweden/Denmark) ———

export const fetchGbifFishObservations = async (
  center: Coordinates,
  bounds: { latMin: number; lonMin: number; latMax: number; lonMax: number },
  countryCode: string,
  signal?: AbortSignal
): Promise<FishObservation[]> => {
  const to = new Date();
  const from = new Date();
  from.setFullYear(from.getFullYear() - FISH_LOOKBACK_YEARS);
  const dateRange = `${from.toISOString().slice(0, 10)},${to.toISOString().slice(0, 10)}`;
  const wkt = `POLYGON((${bounds.lonMin} ${bounds.latMin},${bounds.lonMax} ${bounds.latMin},${bounds.lonMax} ${bounds.latMax},${bounds.lonMin} ${bounds.latMax},${bounds.lonMin} ${bounds.latMin}))`;

  const allResults: any[] = [];
  const promises = FISH_SCIENTIFIC_NAMES.map(async (species) => {
    try {
      const params = new URLSearchParams({
        geometry: wkt,
        country: countryCode,
        eventDate: dateRange,
        scientificName: species,
        limit: '100',
        hasCoordinate: 'true',
        occurrenceStatus: 'PRESENT',
      });
      const res = await fetch(`https://api.gbif.org/v1/occurrence/search?${params}`, {
        signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return [];
      const json = await res.json();
      return json.results || [];
    } catch {
      return [];
    }
  });

  const batchResults = await Promise.all(promises);
  allResults.push(...batchResults.flat());

  const results: FishObservation[] = [];
  const seen = new Set<string>();

  allResults.forEach((obs, idx) => {
    const species = getFishSpecies(obs);
    const loc = parseLocation(obs);
    if (!species || !loc) return;

    const key = `${species}-${loc.latitude.toFixed(3)}-${loc.longitude.toFixed(3)}`;
    if (seen.has(key)) return;
    seen.add(key);

    results.push({
      id: String(obs?.key || obs?.gbifID || `gbif_fish_${idx}`),
      species,
      location: loc,
      observedAt: parseDate(obs),
      locality: typeof obs?.locality === 'string' ? obs.locality : null,
      municipality: typeof obs?.stateProvince === 'string' ? obs.stateProvince : null,
      detailUrl: obs?.key ? `https://www.gbif.org/occurrence/${obs.key}` : null,
    });
  });

  return results;
};

// ——— Aggregate into clusters (~1km grid) ———

export const aggregateFishClusters = (observations: FishObservation[]): FishAbundanceCluster[] => {
  if (observations.length === 0) return [];

  const grid = new Map<string, { obs: FishObservation[]; latSum: number; lonSum: number }>();

  observations.forEach(obs => {
    const gridKey = `${obs.location.latitude.toFixed(2)}-${obs.location.longitude.toFixed(2)}`;
    const cell = grid.get(gridKey) || { obs: [], latSum: 0, lonSum: 0 };
    cell.obs.push(obs);
    cell.latSum += obs.location.latitude;
    cell.lonSum += obs.location.longitude;
    grid.set(gridKey, cell);
  });

  const clusters: FishAbundanceCluster[] = [];

  grid.forEach(cell => {
    const total = cell.obs.length;
    const speciesMap = new Map<string, number>();
    cell.obs.forEach(o => speciesMap.set(o.species, (speciesMap.get(o.species) || 0) + 1));
    const speciesBreakdown = Array.from(speciesMap.entries())
      .map(([species, count]) => ({ species, count }))
      .sort((a, b) => b.count - a.count);

    const score = computeFishScore(total);

    clusters.push({
      center: {
        latitude: cell.latSum / total,
        longitude: cell.lonSum / total,
      },
      totalObservations: total,
      speciesBreakdown,
      scoreLabel: score.label,
      scoreLevel: score.level,
      color: score.color,
    });
  });

  return clusters.filter(c => c.scoreLevel > 0);
};
